/**
 * MIL — beslut 08: milen fylls i FÖR HAND.
 *
 * PROTOTYPEN VISAR "1 050 kr" OCH "Förslag från dagens jobb" MED FÄRDIGA
 * AVSTÅND. Den här skärmen gör varken det ena eller det andra, och båda
 * avstegen har samma rot:
 *
 *   INGET BELOPP. Milbeloppet räknas ur den FRYSTA satsen på raden
 *   (rate_ore_per_10km, beslut 15) — inte ur firmans nuvarande milpris. Den
 *   satsen finns inte i MileageWorkerRow (beslut 16), och att i stället multipli-
 *   cera med dagens pris skulle ge ett tal som SKILJER SIG från fakturan så fort
 *   chefen justerat priset en gång. Ett belopp som nästan stämmer är sämre än
 *   inget belopp. Beloppen står i fakturaunderlaget (INVOICE_BASIS), där de
 *   räknas ur de frysta satserna. Chefen får däremot se FIRMANS PRIS här — det
 *   är en inställning och ingen historik, och raden finns bara i full vy.
 *
 *   INGA AVSTÅND. Kontraktet har ingen hemadress och ingen avståndsberäkning.
 *   Förslagen nedan är därför jobb du RAPPORTERAT TID PÅ i veckan utan att ha
 *   lagt in mil — en genväg till formuläret, inte en gissad sträcka. Ingen
 *   sparar var du är; du skriver siffran själv, precis som beslut 08 säger.
 *
 * STRÄCKA ÄR METER SOM HELTAL: 4,5 mil = 45000. Användaren skriver "4,5".
 */

import { useMemo, useState } from 'react';
import { asMeters } from '@delat/types';
import type { MileageWorkerRow, SyncOp, Vehicle, WorkDate } from '@delat/types';
import {
  FAKTOR,
  Falt,
  Felruta,
  Forslagsrad,
  Installningsrad,
  Knapp,
  Laddar,
  Listrad,
  Sifferblock,
  Snabbrad,
  Talfalt,
  Tomtext,
  Valfalt,
  Veckonavigering,
  Yta,
  tillHeltal,
} from '../ui';
import {
  dagarIVecka,
  idag,
  plusDagar,
  veckaFor,
  visaDag,
  visaMil,
  visaOren,
  visaVecka,
} from '../app/datum';
import { MAX_METER_PER_MILRAD } from '../api/konstanter';
import { useInloggad } from '../app/session';
import { FORDON } from '../api/granskare/tidrapport';
import { useSynk } from './tidrapport/lager';
import type { Ogonblicksbild, Synk } from './tidrapport/lager';
import { felAvOperation, useSparning } from './tidrapport/spara';
import { milradOp, nyttMilradId, raderaMilradOp } from './tidrapport/skriv';
import {
  FORDONSTEXT,
  VECKOLAGETEXT,
  farRapportera,
  arbetsdagUrFalt,
  farSkrivaVecka,
  inomArbetsdagsfonstret,
  orderMedId,
  ordernamn,
  senastArbetsdag,
  sorteradeOrdrar,
  tidigastArbetsdag,
  veckansLage,
} from './tidrapport/regler';

/** CHECK (meters BETWEEN 1 AND 2000000) i schema/company.sql — 200 mil per rad. */
const MAX_METER = MAX_METER_PER_MILRAD;

export const Mil = () => {
  const { firmaId } = useInloggad();
  const synk = useSynk(firmaId);

  if (synk.bild === null) {
    return synk.fel === null ? (
      <Laddar rader={4} />
    ) : (
      <Felruta
        fel={synk.fel}
        atgard={
          <Knapp variant="piller-mini" onClick={() => void synk.synka(true)}>
            Försök igen
          </Knapp>
        }
      />
    );
  }
  return <Milskarmen bild={synk.bild} synk={synk} />;
};

const Milskarmen = ({
  bild,
  synk,
}: {
  readonly bild: Ogonblicksbild;
  readonly synk: Synk;
}) => {
  const { konto, roll, arChef, arLast } = useInloggad();
  const sparning = useSparning(synk.skicka);

  const [ankare, sattAnkare] = useState<WorkDate>(() => idag());
  const [oppet, sattOppet] = useState(false);
  const [orderId, sattOrderId] = useState<string | null>(null);
  const [dag, sattDag] = useState<WorkDate>(() => idag());
  const [miltext, sattMiltext] = useState('');
  const [fordon, sattFordon] = useState<Vehicle>('own_car');
  const [markerad, sattMarkerad] = useState<string | null>(null);

  const vecka = veckaFor(ankare);
  const veckansDagar = dagarIVecka(vecka);
  const ordrar = useMemo(() => sorteradeOrdrar(bild), [bild]);

  const minaRader = useMemo(
    () => bild.milrader.filter((rad) => rad.account_id === konto.id),
    [bild.milrader, konto.id],
  );

  const veckansRader = useMemo(
    () =>
      minaRader
        .filter((rad) => veckansDagar.includes(rad.work_date))
        .sort((a, b) =>
          a.work_date === b.work_date
            ? a.id.localeCompare(b.id)
            : a.work_date.localeCompare(b.work_date),
        ),
    [minaRader, veckansDagar],
  );

  const veckansMeter = veckansRader.reduce((summa, rad) => summa + Number(rad.meters), 0);

  /**
   * FÖRSLAGEN: jobb du rapporterat TID på i veckan men inte lagt in mil för
   * samma dag. Ingen sträcka gissas — knappen fyller bara i jobbet och dagen.
   */
  const forslag = useMemo(() => {
    const harMil = new Set(veckansRader.map((rad) => `${rad.work_order_id}|${rad.work_date}`));
    const ut = new Map<string, { readonly work_order_id: string; readonly dag: WorkDate }>();
    for (const rad of bild.tidrader) {
      if (rad.account_id !== konto.id) continue;
      if (!veckansDagar.includes(rad.work_date)) continue;
      const nyckel = `${rad.work_order_id}|${rad.work_date}`;
      if (harMil.has(nyckel) || ut.has(nyckel)) continue;
      ut.set(nyckel, { work_order_id: rad.work_order_id, dag: rad.work_date });
    }
    return [...ut.values()].sort((a, b) => b.dag.localeCompare(a.dag));
  }, [bild.tidrader, konto.id, veckansDagar, veckansRader]);

  const lage = veckansLage(bild, konto.id, veckaFor(dag));
  const farSkriva =
    farRapportera(roll) &&
    !arLast &&
    inomArbetsdagsfonstret(dag) &&
    farSkrivaVecka({ lage, egen: true, arChef });

  const meter = miltext.trim() === '' ? null : tillHeltal(miltext, FAKTOR.mil_meter);
  const meterDuger = meter !== null && meter >= 1 && meter <= MAX_METER;
  const kanSpara =
    farSkriva && orderId !== null && meterDuger && !sparning.sparar && ordrar.length > 0;

  const andra = <T,>(satt: (varde: T) => void) => (varde: T) => {
    sparning.nollstall();
    satt(varde);
  };

  const oppna = (forvaltOrderId: string | null, forvaldDag: WorkDate): void => {
    sparning.nollstall();
    sattOrderId(forvaltOrderId ?? ordrar[0]?.id ?? null);
    sattDag(forvaldDag);
    sattOppet(true);
  };

  const spara = async (): Promise<void> => {
    const order = orderMedId(bild, orderId);
    if (order === null || meter === null) return;
    const klart = await sparning.spara((): readonly SyncOp[] => [
      milradOp({
        id: nyttMilradId(),
        account_id: konto.id,
        work_date: dag,
        meters: asMeters(meter),
        work_order_id: order.id,
        vehicle: fordon,
        base_version: null,
      }),
    ]);
    if (klart) {
      sattMiltext('');
      sattOppet(false);
      sattAnkare(dag);
    }
  };

  const taBort = (rad: MileageWorkerRow): void => {
    sattMarkerad(null);
    sparning.nollstall();
    void sparning.spara(() => [raderaMilradOp(rad.id, rad.version)]);
  };

  const markeradRad = veckansRader.find((rad) => rad.id === markerad) ?? null;
  const rapporterar = farRapportera(roll);

  return (
    <>
      <Veckonavigering
        vecka={vecka}
        summa={visaMil(veckansMeter)}
        onForegaende={() => {
          sattAnkare(plusDagar(ankare, -7));
          sattMarkerad(null);
        }}
        onNasta={() => {
          sattAnkare(plusDagar(ankare, 7));
          sattMarkerad(null);
        }}
      />

      <Yta>
        <Sifferblock
          varde={visaMil(veckansMeter)}
          under={`Dina mil ${visaVecka(vecka).toLowerCase()} · ${veckansRader.length} ${
            veckansRader.length === 1 ? 'rad' : 'rader'
          }`}
        />
      </Yta>

      {/* BESLUT 16: raden finns bara när vyn är 'full'. Firmans milpris är en
          INSTÄLLNING och inte ett belopp på en rad — beloppen räknas ur de
          frysta satserna och står i fakturaunderlaget. */}
      {bild.milersattning !== null && (
        <Installningsrad
          ikon="mil"
          etikett="Firmans milpris"
          varde={`${visaOren(bild.milersattning)} per mil, ex moms`}
        />
      )}
      {/* MILPRISET 0 SYNS BARA FÖR CHEFEN — beslut 16 skalar bort
          company_settings ur montörens vy, så `milersattning` är null för
          honom och raden ovan finns inte. Den som ser nollan är alltså den
          ENDA som kan rätta den, och då ska det stå rakt ut: satsen fryses på
          varje milrad när den sparas, så det som rapporteras nu blir noll
          kronor för alltid. */}
      {bild.milersattning !== null && Number(bild.milersattning) === 0 && (
        <Tomtext>
          Firmans milpris står på 0 kr. Varje mil som rapporteras nu fryses med
          noll kronor och blir aldrig värd något — sätt priset under
          Inställningar → Timpriser innan laget börjar skriva mil.
        </Tomtext>
      )}

      {forslag.length > 0 && farSkriva && (
        <Yta rubrik="Jobb du varit på i veckan" ikon="plats" rakning={`${forslag.length} st`}>
          {forslag.map((f) => (
            <Forslagsrad
              key={`${f.work_order_id}|${f.dag}`}
              titel={ordernamn(orderMedId(bild, f.work_order_id))}
              varde={visaDag(f.dag)}
              atgard={
                <Knapp
                  variant="piller-mini"
                  onClick={() => oppna(f.work_order_id, f.dag)}
                >
                  Lägg till
                </Knapp>
              }
            />
          ))}
        </Yta>
      )}

      <Yta rubrik={`Inlagt ${visaVecka(vecka).toLowerCase()}`} rakning={visaMil(veckansMeter)}>
        {veckansRader.map((rad) => (
          <Listrad
            key={rad.id}
            titel={ordernamn(orderMedId(bild, rad.work_order_id))}
            bi={`${visaDag(rad.work_date)} · ${FORDONSTEXT[rad.vehicle]}`}
            varde={visaMil(rad.meters)}
            onClick={
              rapporterar && !arLast
                ? () => sattMarkerad(markerad === rad.id ? null : rad.id)
                : undefined
            }
          />
        ))}
        {veckansRader.length === 0 && <Tomtext>Inga mil inlagda den här veckan.</Tomtext>}
      </Yta>

      {markeradRad !== null && (
        <Snabbrad>
          <Knapp variant="snabb" onClick={() => sattMarkerad(null)}>
            Behåll
          </Knapp>
          <Knapp
            variant="snabb"
            disabled={sparning.sparar}
            onClick={() => taBort(markeradRad)}
          >
            Ta bort {visaMil(markeradRad.meters)}
          </Knapp>
        </Snabbrad>
      )}

      {rapporterar && !arLast && !oppet && (
        <Knapp variant="stor" ikon="mil" onClick={() => oppna(null, idag())}>
          Lägg till mil
        </Knapp>
      )}

      {oppet && (
        <Yta rubrik="Ny milrad">
          {ordrar.length === 0 ? (
            <Tomtext>
              Firman har inga arbetsordrar än. En milrad måste höra till ett jobb.
            </Tomtext>
          ) : (
            <>
              <Valfalt
                etikett="Jobbet"
                varde={orderId ?? ''}
                onAndra={(id) => andra(sattOrderId)(id)}
                val={ordrar.map((o) => ({ varde: String(o.id), etikett: ordernamn(o) }))}
              />
              <Talfalt
                etikett="Hur långt?"
                varde={miltext}
                onAndra={(text) => andra(sattMiltext)(text)}
                faktor={FAKTOR.mil_meter}
                suffix="mil"
                placeholder="4,5"
                hjalp="En mil är 10 km. Skriv 4,5 för fyra och en halv mil."
                fel={
                  miltext.trim() !== '' && !meterDuger
                    ? 'Skriv sträckan i mil, högst 200 mil per rad.'
                    : undefined
                }
              />
              <Valfalt
                etikett="Bil"
                varde={fordon}
                onAndra={(v) => andra(sattFordon)(v)}
                val={FORDON.map((f) => ({ varde: f, etikett: FORDONSTEXT[f] }))}
                hjalp="Bara upplysning. Milersättningen är firmans och ändras inte av bilen."
              />
              <Falt
                etikett="Dagen"
                type="date"
                varde={dag}
                min={tidigastArbetsdag()}
                max={senastArbetsdag()}
                onAndra={(text) => {
                  const vald = arbetsdagUrFalt(text);
                  if (vald === null) return;
                  sparning.nollstall();
                  sattDag(vald);
                }}
              />
              <Knapp variant="stor" disabled={!kanSpara} onClick={() => void spara()}>
                {sparning.sparar ? 'Sparar ...' : 'Spara milen'}
              </Knapp>
              <Knapp variant="stor-tyst" onClick={() => sattOppet(false)}>
                Avbryt
              </Knapp>
            </>
          )}
        </Yta>
      )}

      {sparning.fel !== null && <Felruta fel={sparning.fel} />}
      {sparning.fallna.map((rad) => (
        <Felruta key={rad.op_id} fel={felAvOperation(rad)} />
      ))}

      {oppet && !farSkriva && rapporterar && !arLast && (
        <Tomtext>
          Veckan för den valda dagen är {VECKOLAGETEXT[lage].toLowerCase()} och kan inte
          ändras. Välj en annan dag eller be chefen öppna veckan.
        </Tomtext>
      )}
      {arLast && (
        <Tomtext>
          Prenumerationen är pausad. Allt går att läsa och exportera, men inget nytt
          kan sparas förrän betalningen är ordnad.
        </Tomtext>
      )}

      <Tomtext>
        Milen skrivs för hand. Ingen spårar var du är, och appen gissar inga
        sträckor — du skriver siffran och kan ändra den. Vad milen är VÄRD står i
        fakturaunderlaget, där den räknas ur den sats som frystes på raden.
      </Tomtext>
    </>
  );
};
